import { useState } from "react";
import { PDFDocument } from "pdf-lib";
import { renderPdfThumbnails, downloadBlob } from "../Helper/pdfThumpnails";
import Text from "./Text";
import Button from "./Buttons/Button";

export default function PDFPageManager() {
    const [fileName, setFileName] = useState("");
    const [pdfBytes, setPdfBytes] = useState(null);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setError("");
        setLoading(true);
        try {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            const thumbs = await renderPdfThumbnails(file, 0.35);

            setFileName(file.name);
            setPdfBytes(bytes);
            setPages(
                thumbs.map((dataUrl, i) => ({
                    id: `${i}-${Date.now()}`,
                    originalIndex: i,
                    dataUrl,
                }))
            );
        } catch (err) {
            console.error(err);
            setError("PDF konnte nicht gelesen werden.");
        } finally {
            setLoading(false);
        }
    };

    const moveItem = (index, direction) => {
        setPages((prev) => {
            const copy = [...prev];
            const target = index + direction;
            if (target < 0 || target >= copy.length) return prev;
            [copy[index], copy[target]] = [copy[target], copy[index]];
            return copy;
        });
    };

    const removePage = (index) => {
        setPages((prev) => prev.filter((_, i) => i !== index));
    };

    const savePdf = async () => {
        if (!pdfBytes || pages.length === 0) {
            setError("Mindestens eine Seite muss übrig bleiben.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const srcDoc = await PDFDocument.load(pdfBytes);
            const newDoc = await PDFDocument.create();

            for (const p of pages) {
                const [copied] = await newDoc.copyPages(srcDoc, [p.originalIndex]);
                newDoc.addPage(copied);
            }

            const outBytes = await newDoc.save();
            downloadBlob(
                outBytes,
                fileName.replace(/\.pdf$/i, "") + "-bearbeitet.pdf",
                "application/pdf"
            );
        } catch (err) {
            console.error(err);
            setError("Fehler beim Erstellen der PDF.");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setFileName("");
        setPdfBytes(null);
        setPages([]);
        setError("");
    };

    return (
        <div className="flex flex-col gap-4">
            <Text type="body">
                PDF auswählen, um die Reihenfolge der Seiten zu ändern oder einzelne Seiten zu löschen.
            </Text>

            {!fileName && (
                <input type="file" accept="application/pdf" onChange={handleFileChange} />
            )}

            {loading && <Text type="body">Seiten werden geladen...</Text>}

            {fileName && !loading && (
                <div className="flex items-center justify-between">
                    <Text type="body" className="text-gray-500">{fileName} · {pages.length} Seiten</Text>
                    <Button buttonColor="bg-gray-200" onClickAction={reset}>
                        Andere Datei wählen
                    </Button>
                </div>
            )}

            {pages.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {pages.map((p, index) => (
                        <div
                            key={p.id}
                            className="relative border rounded-md p-2 flex flex-col items-center gap-2 bg-white"
                        >
                            <button
                                onClick={() => removePage(index)}
                                title="Seite löschen"
                                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center shadow"
                            >
                                ✕
                            </button>

                            <div className="flex items-center justify-center h-32 w-full overflow-hidden border border-gray-100 rounded">
                                <img
                                    src={p.dataUrl}
                                    alt={`Seite ${index + 1}`}
                                    className="max-h-full max-w-full"
                                />
                            </div>

                            <span className="text-xs text-gray-500">
                                Position {index + 1} (urspr. Seite {p.originalIndex + 1})
                            </span>

                            <div className="flex gap-1">
                                <Button
                                    buttonColor="bg-blue-100"
                                    onClickAction={() => moveItem(index, -1)}
                                    disabled={index === 0}
                                >
                                    ↑
                                </Button>
                                <Button
                                    buttonColor="bg-blue-100"
                                    onClickAction={() => moveItem(index, 1)}
                                    disabled={index === pages.length - 1}
                                >
                                    ↓
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {error && <p style={{ color: "red" }}>{error}</p>}

            {pages.length > 0 && (
                <Button
                    buttonColor={saving ? "bg-gray-400" : "bg-green-100"}
                    onClickAction={savePdf}
                    disabled={saving}
                >
                    {saving ? "Wird erstellt..." : "Als PDF speichern"}
                </Button>
            )}
        </div>
    );
}